/**
 * Plans Chat API service — dual-model (Gemini + Claude) for travel plan creation/editing.
 */

const API_BASE = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '' : 'http://localhost:8000');

/** The device's IANA timezone — the server resolves "today"/"tomorrow" in
 *  chat against THIS, not its own clock (a 9pm Pacific "today" is already
 *  the server's tomorrow). Empty string when the runtime cannot say, which
 *  the server treats as absent and degrades to its own zone. */
function deviceTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  } catch {
    return '';
  }
}

export type PlansChatType = 'plan_create' | 'plan_edit' | 'on_trip' | 'quick_question';
export type PlansChatEntryIntent = 'new_plan' | 'resume_discovery' | 'edit_itinerary';

/** One committed change, named the way the reader would name it. */
export interface PlanChange {
  op: 'add' | 'update' | 'move' | 'remove';
  item_id?: number;
  name?: string;
  date?: string;
  fields?: string[];
}

/**
 * Present ONLY on a turn whose plan build failed (already retried once
 * server-side). `retryable` says whether resending the same message can help
 * — the "Try again" keys on it, never on the reply's copy. Absent otherwise.
 */
export interface BuildFailed {
  site: string;
  retryable: boolean;
}

export interface PlansChatResponseData {
  text: string;
  build_failed?: BuildFailed;
  itinerary: Record<string, unknown> | null;
  showUndo: boolean;
  itinerary_id: number | null;
  /** True only when this turn created a new itinerary (analytics signal). */
  created?: boolean;
  content_revision?: number | null;
  mutation_report?: {
    committed: boolean;
    content_revision: number;
    operations?: PlanChange[];
    summary: string;
  } | null;
  /**
   * Present ONLY on a turn that staged a change proposal (approve-before-
   * apply): a reference, never the card — the card is fetched by
   * hydration so the server's retention scrub has one copy to null.
   * Absent (not null) on every other turn.
   */
  proposal_ref?: { id: number; status_at_delivery: string   /** The server-side deadline (arms mobile's bar timer; web does not
   *  consume it yet — carried for contract parity). */
  expires_at?: string;
};
}

/**
 * Capabilities this bundle declares to the plans-chat endpoint. A constant
 * on the wire — deliberately NOT stored in the persisted retry envelope, so
 * an idempotent replay after a deploy sends the same receipt-hashed request
 * body it originally sent (capabilities are a client property, outside the
 * idempotency hash server-side).
 */
export const PLANS_CHAT_CAPABILITIES = ['plan_change_proposals'];

export type PlanProposalStatus =
  | 'pending' | 'applied' | 'rejected' | 'superseded' | 'conflicted'
  | 'expired';


/** One structured change row (`render_change_rows`): the SAME derivation the
 *  text card is joined from, so the two cannot disagree. `head`/`extras`
 *  are content and MUST be rendered as text nodes — the server quotes them
 *  as data, it does not HTML-escape, because this sink contract is the
 *  guarantee. */
export interface ChangeRow {
  kind: string;
  marker: string;
  head: string;
  extras: string[];
}

export interface PlanProposal {
  id: number;
  status: PlanProposalStatus;
  /** Newline-separated change lines; null once the retention scrub ran. */
  card: string | null;
  /** Structured rows for rich rendering; null when scrubbed AND for
   *  proposals created before the column existed — fall back to `card`. */
  card_rows: ChangeRow[] | null;
  content_scrubbed: boolean;
  itinerary_id: number;
  base_content_revision: number;
  mutation_report: PlansChatResponseData['mutation_report'];
  created_at: string;
  expires_at: string;
  /** Outcome copy on approve/reject/replay responses (server-authored). */
  message?: string;
}

/**
 * Verbatim client copy for a scrubbed hydration (bound by a backend test):
 * state-neutral, because state-specific copy would misrepresent four of the
 * five terminal states.
 */
export const SCRUBBED_PLACEHOLDER =
  "This suggestion's details are no longer stored.";

export async function getProposal(id: number): Promise<PlanProposal> {
  return fetchApi<PlanProposal>(`/api/planner/proposals/${id}/`);
}

export async function approveProposal(id: number): Promise<PlanProposal> {
  return fetchApi<PlanProposal>(`/api/planner/proposals/${id}/approve/`, {
    method: 'POST',
    // The approver's zone: the server re-runs the trip-under-way guard at
    // apply time on the APPROVER's day (a pending proposal outlives its
    // validation), and without this it falls back to the server clock.
    body: JSON.stringify({ client_tz: deviceTimeZone() || undefined }),
  });
}

export async function rejectProposal(id: number): Promise<PlanProposal> {
  return fetchApi<PlanProposal>(`/api/planner/proposals/${id}/reject/`, {
    method: 'POST',
  });
}

export interface PlansChatApiResponse {
  conversation_id: string;
  response: PlansChatResponseData;
  // Legacy
  soft_gate?: boolean;
  gate_type?: 'signup' | 'upgrade';
  // Unified gate
  gate?: 'auth' | 'upgrade' | null;
  msg_count?: number;
  msg_limit?: number;
}

export interface UndoResponse {
  success: boolean;
  deleted?: boolean;
  message?: string;
  itinerary?: Record<string, unknown>;
  items?: Record<string, unknown>[];
}

export class PlansChatApiError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly body: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = 'PlansChatApiError';
  }
}

function getCsrfToken(): string | null {
  const cookies = document.cookie.split(';');
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=');
    if (name === 'csrftoken') return value;
  }
  return null;
}

async function fetchApi<T>(endpoint: string, options: RequestInit = {}, timeoutMs?: number): Promise<T> {
  const url = `${API_BASE}${endpoint}`;
  const headers: Record<string, string> = { 'Content-Type': 'application/json' };

  const method = options.method?.toUpperCase() || 'GET';
  if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
    const csrfToken = getCsrfToken();
    if (csrfToken) headers['X-CSRFToken'] = csrfToken;
  }

  // Abort long-hanging requests so the user never stares at an endless
  // typing indicator (plan builds are bounded server-side at 90s).
  let signal = options.signal;
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  if (timeoutMs && !signal) {
    const controller = new AbortController();
    signal = controller.signal;
    timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  }

  try {
    const response = await fetch(url, {
      ...options,
      credentials: 'include',
      headers: { ...headers, ...options.headers as Record<string, string> },
      signal,
    });

    if (!response.ok) {
      let error: Record<string, unknown>;
      try {
        error = await response.json();
      } catch {
        throw new PlansChatApiError('Response body could not be read', 0);
      }
      throw new PlansChatApiError(
        typeof error.error === 'string' ? error.error : `API error: ${response.status}`,
        response.status, error,
      );
    }

    try {
      return await response.json();
    } catch {
      throw new PlansChatApiError('Response body could not be read', 0);
    }
  } finally {
    // The timeout owns the complete response body, not merely the headers.
    if (timeoutId) clearTimeout(timeoutId);
  }
}

// Server-side plan builds are capped at 90s; give the network a little headroom.
const CHAT_TIMEOUT_MS = 120000;

export async function sendPlansChatMessage(
  message: string,
  conversationId?: string | null,
  itineraryId?: number | null,
  chatType?: PlansChatType,
  entryIntent?: PlansChatEntryIntent,
  entryId?: string,
  turnId?: string,
  expectedRevision?: number | null,
): Promise<PlansChatApiResponse> {
  return fetchApi<PlansChatApiResponse>('/api/planner/plans-chat/', {
    method: 'POST',
    body: JSON.stringify({
      message,
      conversation_id: conversationId || undefined,
      itinerary_id: itineraryId || undefined,
      chat_type: chatType || 'plan_create',
      entry_intent: entryIntent,
      entry_id: entryId,
      turn_id: turnId,
      expected_revision: expectedRevision,
      client_capabilities: PLANS_CHAT_CAPABILITIES,
      client_tz: deviceTimeZone() || undefined,
    }),
  }, CHAT_TIMEOUT_MS);
}

export interface ItineraryConversation {
  id: string;
  messages: Array<{ role: string; content: string }>;
  itinerary_id: number | null;
}

/**
 * One conversation by id — the authoritative transcript.
 *
 * Turn recovery needs this on the discovery surface too, where there is no
 * itinerary to look the conversation up by and web's only transcript is the
 * local one. The endpoint is user-scoped and has existed since the chat was
 * built; mobile has always called it.
 */
export function getPlansConversation(
  conversationId: string, timeoutMs?: number,
): Promise<ItineraryConversation> {
  return fetchApi<ItineraryConversation>(
    `/api/planner/chat/${conversationId}/`, {}, timeoutMs,
  );
}

/**
 * This plan's own plans-chat conversation, or null when it has none yet.
 *
 * The endpoint has existed since the in-plan chat was built but only mobile
 * ever called it, so on web every "Edit with AI" opened a blank window while
 * the server-side conversation continued underneath — the model held context
 * the user could not see.
 */
export async function getItineraryConversation(
  itineraryId: number,
): Promise<ItineraryConversation | null> {
  const response = await fetch(
    `${API_BASE}/api/planner/itineraries/${itineraryId}/conversation/`,
    { credentials: 'include' },
  );
  // 204 is the documented "no resumable conversation" answer.
  if (response.status === 204 || !response.ok) return null;
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function undoPlan(
  itineraryId: number, expectedRevision?: number | null,
): Promise<UndoResponse> {
  return fetchApi<UndoResponse>(`/api/planner/itineraries/${itineraryId}/undo/`, {
    method: 'POST',
    body: JSON.stringify({ expected_revision: expectedRevision }),
  });
}

// Anonymous chat API

export interface AnonymousChatResponse {
  text: string;
  build_failed?: BuildFailed;
  messages: Array<{ role: string; content: string }>;
  pending_plan: Record<string, unknown> | null;
  anonymous_token: string;
  msg_count: number;
  msg_limit: number;
  gate: 'auth_required' | null;
}

export async function sendAnonymousChatMessage(
  message: string,
  messages: Array<{ role: string; content: string }>,
  anonymousToken?: string | null,
): Promise<AnonymousChatResponse> {
  return fetchApi<AnonymousChatResponse>('/api/planner/plans-chat/session/', {
    method: 'POST',
    body: JSON.stringify({
      message,
      messages,
      anonymous_token: anonymousToken || undefined,
      client_tz: deviceTimeZone() || undefined,
    }),
  }, CHAT_TIMEOUT_MS);
}

export interface ResolveSessionResponse {
  itinerary?: Record<string, unknown>;
  itinerary_id?: number;
  conversation_id?: string;
  plan_error?: string;
}

export async function resolveAnonymousSession(
  messages: Array<{ role: string; content: string }>,
  pendingPlan: Record<string, unknown> | null,
  anonymousToken?: string | null,
  entryId?: string | null,
): Promise<ResolveSessionResponse> {
  return fetchApi<ResolveSessionResponse>('/api/planner/plans-chat/resolve/', {
    method: 'POST',
    body: JSON.stringify({
      messages,
      pending_plan: pendingPlan,
      anonymous_token: anonymousToken || undefined,
      entry_id: entryId || undefined,
    }),
  }, CHAT_TIMEOUT_MS);
}

export async function mergeAnonymousCounter(anonymousToken: string): Promise<{ merged: boolean; msg_count?: number }> {
  return fetchApi('/auth/merge-anonymous-counter/', {
    method: 'POST',
    body: JSON.stringify({ anonymous_token: anonymousToken }),
  });
}

// === Plan from a photo (docs/CAPTURE_TO_PLAN_PLAN.md §4) ===

/** `GET plans-chat/capture/config/` — always 200, for every caller. */
export interface CaptureConfig {
  enabled: boolean;
  max_sources: number;
  max_bytes_each: number;
  max_request_bytes: number;
  max_ideas: number;
}

export type CaptureSourceKind =
  | 'booklet' | 'magazine' | 'email' | 'website' | 'ad' | 'billboard' | 'other' | 'none';
export type CaptureTripType =
  | 'city' | 'beach' | 'nature' | 'road_trip' | 'cruise' | 'resort' | 'multi_city' | 'other';
export type CaptureSeason = 'spring' | 'summer' | 'autumn' | 'winter' | 'any';

/** One idea as the server validated and composed it. Only `request_text`
 *  is ever sent anywhere; the rest is display. */
export interface CaptureIdea {
  destination: string;
  trip_type: CaptureTripType;
  season: CaptureSeason;
  month: number | null;
  days: number | null;
  highlights: string[];
  request_text: string;
}

export interface CaptureResponse {
  source_kind: CaptureSourceKind;
  ideas: CaptureIdea[];
  gate: 'auth' | 'upgrade' | null;
  msg_count?: number;
  msg_limit?: number;
}

export async function getCaptureConfig(): Promise<CaptureConfig> {
  return fetchApi<CaptureConfig>('/api/planner/plans-chat/capture/config/');
}

// The provider call is bounded at 60s server-side; give the upload headroom.
const CAPTURE_TIMEOUT_MS = 90000;

/**
 * `POST plans-chat/capture/` — multipart field `sources`. Not `fetchApi`:
 * that sets a JSON `Content-Type`, and a multipart body needs the browser
 * to write its own boundary. CSRF rides the header, as the activity-photo
 * upload does. Errors surface as `PlansChatApiError` with the HTTP status
 * and the server's body (`{error: code}`, or DRF's `{detail}` on 403/429).
 */
export async function captureIdeas(files: File[]): Promise<CaptureResponse> {
  const form = new FormData();
  files.forEach((file) => form.append('sources', file));
  const headers: Record<string, string> = {};
  const csrfToken = getCsrfToken();
  if (csrfToken) headers['X-CSRFToken'] = csrfToken;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), CAPTURE_TIMEOUT_MS);
  try {
    const response = await fetch(`${API_BASE}/api/planner/plans-chat/capture/`, {
      method: 'POST',
      credentials: 'include',
      headers,
      body: form,
      signal: controller.signal,
    });
    if (!response.ok) {
      let body: Record<string, unknown> = {};
      try { body = await response.json(); } catch { /* status alone decides */ }
      throw new PlansChatApiError(
        typeof body.error === 'string' ? body.error : `API error: ${response.status}`,
        response.status, body,
      );
    }
    try {
      return await response.json();
    } catch {
      throw new PlansChatApiError('Response body could not be read', 0);
    }
  } finally {
    clearTimeout(timeoutId);
  }
}
